/**
 * Renders a dynamic form from a JSON schema provided by the agent's collect_fields tool.
 * Maps field types to Meridian components. Submits form data as JSON.
 */
import React, { useState } from 'react';
import Button from '@amzn/meridian/button';
import Input from '@amzn/meridian/input';
import Select, { SelectOption } from '@amzn/meridian/select';
import Toggle from '@amzn/meridian/toggle';
import Text from '@amzn/meridian/text';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';

export const VALID_FIELD_TYPES = ['text', 'number', 'select', 'multiselect', 'date', 'toggle', 'array'] as const;
export type FieldType = typeof VALID_FIELD_TYPES[number];

interface FormField {
  readonly name: string;
  readonly label: string;
  readonly type: FieldType;
  readonly description?: string;
  readonly required?: boolean;
  readonly defaultValue?: unknown;
  readonly options?: ReadonlyArray<{ label: string; value: string }>;
  readonly placeholder?: string;
  readonly selectAll?: boolean;
  readonly constraints?: { min?: number; max?: number; maxLength?: number; minItems?: number; maxItems?: number };
}

interface FormSchema {
  readonly title: string;
  readonly description?: string;
  readonly submitLabel?: string;
  readonly secondaryLabel?: string;
  readonly fields: readonly FormField[];
}

interface DynamicFormProps {
  readonly schema: FormSchema;
  readonly onSubmit: (data: Record<string, unknown>) => void;
  readonly disabled?: boolean;
  readonly submittedValues?: Readonly<Record<string, unknown>>;
}

const getStringArrayDefault = (defaultValue: unknown): string[] => (
  Array.isArray(defaultValue)
    ? defaultValue.filter((value): value is string => typeof value === 'string')
    : []
);

const isValidFieldValue = (field: FormField, value: unknown): boolean => {
  if (field.type === 'array' || field.type === 'multiselect') {
    return Array.isArray(value) && value.every(item => typeof item === 'string');
  }
  if (field.type === 'toggle') return typeof value === 'boolean';
  if (field.type === 'select' || field.type === 'date') return typeof value === 'string';
  if (field.type === 'text' || field.type === 'number') {
    return typeof value === 'string' || typeof value === 'number';
  }
  return false;
};

const getInitialValue = (field: FormField, submittedValue?: unknown): unknown => {
  const initialValue = isValidFieldValue(field, submittedValue) ? submittedValue : field.defaultValue;
  if (field.type === 'array') {
    const values = getStringArrayDefault(initialValue);
    return values.length > 0 ? values : [''];
  }
  if (field.type === 'multiselect') return getStringArrayDefault(initialValue);
  if (field.type === 'toggle') return typeof initialValue === 'boolean' ? initialValue : false;
  if (field.type === 'select' || field.type === 'date') return typeof initialValue === 'string' ? initialValue : '';
  if (field.type === 'text' || field.type === 'number') {
    return typeof initialValue === 'string' || typeof initialValue === 'number' ? initialValue : '';
  }
  return '';
};

const getFieldError = (field: FormField, value: unknown): string | undefined => {
  const arrayValues = field.type === 'array'
    ? getStringArrayDefault(value).map(item => item.trim()).filter(Boolean)
    : [];
  let error: string | undefined;

  if (field.required) {
    if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '')) error = 'Required';
    else if (field.type === 'multiselect' && Array.isArray(value) && value.length === 0) error = 'Required';
    else if (field.type === 'array' && arrayValues.length === 0) error = 'Required';
  }
  if (field.type === 'number' && value !== '' && value !== undefined) {
    const numberValue = Number(value);
    if (Number.isNaN(numberValue)) error = 'Must be a number';
    else if (field.constraints?.min !== undefined && numberValue < field.constraints.min) error = `Min ${field.constraints.min}`;
    else if (field.constraints?.max !== undefined && numberValue > field.constraints.max) error = `Max ${field.constraints.max}`;
  }
  if (field.type === 'text') {
    const maxLength = field.constraints?.maxLength;
    if (maxLength !== undefined && String(value ?? '').trim().length > maxLength) error = `Max ${maxLength} characters`;
  }
  if (field.type === 'array') {
    const { minItems, maxItems, maxLength } = field.constraints ?? {};
    if (minItems !== undefined && arrayValues.length < minItems) error = `At least ${minItems} ${minItems === 1 ? 'item' : 'items'}`;
    else if (maxItems !== undefined && arrayValues.length > maxItems) error = `At most ${maxItems} ${maxItems === 1 ? 'item' : 'items'}`;
    else if (maxLength !== undefined && arrayValues.some(item => item.length > maxLength)) error = `Max ${maxLength} characters`;
  }
  return error;
};

const getSubmissionValues = (
  fields: readonly FormField[],
  values: Readonly<Record<string, unknown>>,
  shouldEmptyArrayFields: boolean,
): Record<string, unknown> => {
  const fieldTypes = new Map(fields.map(field => [field.name, field.type]));
  const cleaned: Record<string, unknown> = {};
  for (const [name, value] of Object.entries(values)) {
    const fieldType = fieldTypes.get(name);
    if (fieldType === 'array') {
      cleaned[name] = shouldEmptyArrayFields
        ? []
        : getStringArrayDefault(value).map(item => item.trim()).filter(Boolean);
    } else if (fieldType === 'text' && typeof value === 'string') {
      cleaned[name] = value.trim();
    } else if (fieldType === 'number') {
      // Number fields hold a raw string while editing (so decimals like "2.5" are typeable); coerce to
      // a Number at submit. Empty stays empty.
      const raw = typeof value === 'string' ? value.trim() : value;
      cleaned[name] = (raw === '' || raw === undefined || raw === null) ? '' : Number(raw);
    } else {
      cleaned[name] = value;
    }
  }
  return cleaned;
};

export const DynamicForm: React.FC<DynamicFormProps> = ({ schema, onSubmit, disabled, submittedValues }) => {
  const [submitted, setSubmitted] = useState(false);
  const [values, setValues] = useState<Record<string, unknown>>(() => {
    const initial: Record<string, unknown> = {};
    for (const field of schema.fields) {
      const submittedValue = disabled ? submittedValues?.[field.name] : undefined;
      initial[field.name] = getInitialValue(field, submittedValue);
    }
    return initial;
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const setValue = (name: string, value: unknown): void => {
    setValues(prev => ({ ...prev, [name]: value }));
    setErrors(prev => { const { [name]: _, ...rest } = prev; return rest; });
  };

  const validate = (shouldSkipArrayValidation: boolean): boolean => {
    const nextErrors: Record<string, string> = {};
    for (const field of schema.fields) {
      if (shouldSkipArrayValidation && field.type === 'array') continue;
      const error = getFieldError(field, values[field.name]);
      if (error) nextErrors[field.name] = error;
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (shouldEmptyArrayFields: boolean): void => {
    if (submitted || disabled) return;
    if (!validate(shouldEmptyArrayFields)) return;
    const submissionValues = getSubmissionValues(schema.fields, values, shouldEmptyArrayFields);
    setValues(submissionValues);
    setSubmitted(true);
    onSubmit(submissionValues);
  };

  const handlePrimarySubmit = (): void => { handleSubmit(false); };
  const handleSecondarySubmit = (): void => { handleSubmit(true); };

  const renderField = (field: FormField): React.ReactNode => {
    const val = values[field.name];
    const error = errors[field.name];
    const inputId = `dynamic-form-field-input-${field.name}`;
    const descriptionId = field.description ? `dynamic-form-field-description-${field.name}` : undefined;
    const getAriaDescribedBy = (controlId: string, hasError: boolean): string | undefined => (
      [descriptionId, hasError ? `${controlId}-error-message` : undefined].filter(Boolean).join(' ') || undefined
    );

    switch (field.type) {
      case 'text':
      case 'number':
      case 'date':
        return (
          <Input
            aria-describedby={getAriaDescribedBy(inputId, !!error)}
            disabled={disabled}
            error={!!error}
            errorMessage={error}
            id={inputId}
            // Number fields render as text with a decimal inputMode: a native type="number" applies an
            // implicit step=1 that rejects decimals (e.g. a target CPC of 2.50). We keep the raw string
            // as the value (coercing on every keystroke would turn "2." into 2, making "." untypeable);
            // validation and submission parse it via Number().
            inputMode={field.type === 'number' ? 'decimal' : undefined}
            placeholder={field.placeholder}
            type={field.type === 'date' ? 'date' : 'text'}
            value={String(val ?? '')}
            onChange={(v: string) => { setValue(field.name, v); }}
          />
        );
      case 'select':
        return (
          <Select
            aria-describedby={getAriaDescribedBy(inputId, !!error)}
            disabled={disabled}
            error={!!error}
            errorMessage={error}
            id={inputId}
            value={val as string}
            onChange={(v: string) => { setValue(field.name, v); }}
          >
            {field.options?.filter(opt => opt.value != null && opt.label != null).map(opt => (
              <SelectOption key={opt.value} label={opt.label} value={String(opt.value)} />
            ))}
          </Select>
        );
      case 'multiselect': {
        interface SelectValueLabelOption {
          readonly value: string;
          readonly label: string;
        }

        const current = Array.isArray(val) ? val as string[] : [];
        const optionValues = (field.options ?? []).map(opt => String(opt.value));
        const allSelected = optionValues.length > 0 && optionValues.every(v => current.includes(v));
        const SELECT_ALL = '__select_all__';
        const displayValue = field.selectAll && allSelected ? [SELECT_ALL, ...current] : current;

        const handleMultiChange = (v: string[]): void => {
          const arr = Array.isArray(v) ? v : [v];
          const hadAll = displayValue.includes(SELECT_ALL);
          const hasAll = arr.includes(SELECT_ALL);
          if (!hadAll && hasAll) {
            setValue(field.name, optionValues);
          } else if (hadAll && !hasAll) {
            setValue(field.name, []);
          } else {
            setValue(field.name, arr.filter(x => x !== SELECT_ALL));
          }
        };

        return (
          <Select
            aria-describedby={getAriaDescribedBy(inputId, !!error)}
            disabled={disabled}
            error={!!error}
            errorMessage={error}
            id={inputId}
            value={displayValue}
            valueLabel={field.selectAll
              ? (options: readonly SelectValueLabelOption[]) => {
                  const realOptions = options.filter(option => option.value !== SELECT_ALL);
                  return realOptions.length === 1
                    ? realOptions[0].label
                    : realOptions.length > 0 ? `${realOptions.length} selected` : '';
                }
              : undefined}
            onChange={handleMultiChange}
          >
            {field.selectAll && (
              <SelectOption key={SELECT_ALL} label="Select all" value={SELECT_ALL} />
            )}
            {field.options?.filter(opt => opt.value != null && opt.label != null).map(opt => (
              <SelectOption key={opt.value} label={opt.label} value={String(opt.value)} />
            ))}
          </Select>
        );
      }
      case 'toggle':
        return (
          <Column spacing="100">
            <Toggle
              aria-describedby={getAriaDescribedBy(inputId, !!error)}
              checked={val as boolean}
              disabled={disabled}
              id={inputId}
              onChange={(v: boolean) => { setValue(field.name, v); }}
            />
            {error && <Text color="error" id={`${inputId}-error-message`} type="b100">{error}</Text>}
          </Column>
        );
      case 'array': {
        const items = (val as string[] | undefined) ?? [''];
        const maxLength = field.constraints?.maxLength;
        const hasMaxLengthError = maxLength !== undefined && error === `Max ${maxLength} characters`;
        return (
          <Column spacing="200">
            {items.map((item, i) => {
              const itemInputId = `${inputId}-${i}`;
              const isItemOverMaxLength = maxLength !== undefined && item.trim().length > maxLength;
              const hasItemError = hasMaxLengthError ? isItemOverMaxLength : i === 0 && !!error;
              return (
                <Row key={i} spacing="200">
                  <div style={{ flex: 1 }}>
                    <Input
                      aria-describedby={getAriaDescribedBy(itemInputId, hasItemError)}
                      disabled={disabled}
                      error={hasItemError}
                      errorMessage={hasItemError ? error : undefined}
                      id={itemInputId}
                      placeholder={field.placeholder}
                      value={item}
                      onChange={(v: string) => {
                        const next = [...items];
                        next[i] = v;
                        setValue(field.name, next);
                      }}
                    />
                  </div>
                  {!disabled && items.length > 1 && (
                    <Button size="small" type="link" onClick={() => { setValue(field.name, items.filter((_, idx) => idx !== i)); }}>✕</Button>
                  )}
                </Row>
              );
            })}
            {!disabled && (!field.constraints?.maxItems || items.length < field.constraints.maxItems) && (
              <Button size="small" type="link" onClick={() => { setValue(field.name, [...items, '']); }}>+ Add</Button>
            )}
          </Column>
        );
      }
      default:
        return null;
    }
  };

  const primarySubmitButton = (
    <Button data-testid="dynamic-form-submit" disabled={submitted || disabled} type="primary" onClick={handlePrimarySubmit}>
      {schema.submitLabel ?? 'Submit'}
    </Button>
  );

  return (
    <div data-testid="dynamic-form">
      <Column spacing="400">
        <Text type="h100">{schema.title}</Text>
        {schema.description && <Text color="secondary">{schema.description}</Text>}
        {schema.fields.map(field => (
          <Column key={field.name} spacing="100">
            <Text type="b200">{field.label}{field.required ? ' *' : ''}</Text>
            {field.description && (
              <Text
                color="secondary"
                data-testid={`dynamic-form-field-description-${field.name}`}
                id={`dynamic-form-field-description-${field.name}`}
                type="b100"
              >
                <span style={{ fontStyle: 'italic' }}>{field.description}</span>
              </Text>
            )}
            {renderField(field)}
          </Column>
        ))}
        {schema.secondaryLabel
          ? (
            <Row spacing="200">
              {primarySubmitButton}
              <Button data-testid="dynamic-form-secondary" disabled={submitted || disabled} type="secondary" onClick={handleSecondarySubmit}>
                {schema.secondaryLabel}
              </Button>
            </Row>
            )
          : primarySubmitButton}
      </Column>
    </div>
  );
};
